import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useParams } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faEye, faPrint, faTrash } from "@fortawesome/free-solid-svg-icons";
import { InvoicePaymentApi, InvoicePaymentDataType } from "@/api/InvoicePaymentApi";
import { formatMoneyValue } from "@utils/Currency";
import { showAlert } from "@utils/Alert";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { Col, Container, Row } from "reactstrap";
import { InvoiceFormDataKeys } from "@/generated/InvoiceFormDataKeys";
import { InvoiceRelatedAttributes } from "@/formTypes/InvoiceDataType";
import { AmplifyFilter, formatDate } from "@utils/Api";
import LoadingScreen from "@components/content/LoadingScreen";
import { getPaymentMethodLabel } from "@utils/Utils";
import { FetchResponse } from "@/api/ApiClient";
import { CreditnoteDataType } from "@/api/CreditnoteApi";
import { isValidEDocument } from "@utils/EDocument";
import { DebitnoteDataType } from "@/api/DebitnoteApi";
import { INVOICE_HOLDING_TYPE_CODE, INVOICE_PAYMENT_ACTIVE_STATUS, INVOICE_PAYMENT_CANCELLED_STATUS, OTHER_PAYMENT_WAY_CODE, TICKET_PRINT_FORMANT_ID } from "@utils/Constants";
import InvoicePaymentOffCanvas from "@components/layout/Offcanvas/InvoicePaymentOffCanvas";
import { useAppSelector } from "@/store/hooks";
import { OpenInvoicePaymentPubSub } from "@utils/pubsubs/PubSub";
import { checkInvoicePaymentTicketPrint } from "@utils/ticketPrintUtils";

const InvoicePaymentIndex = () => {
  const params = useParams();
  const modelRef = useRef<ModelIndexHandle>(null);
  const { invoiceId } = params;
  const [invoice, setInvoice] = useState<InvoiceDataType>({})
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paymentAmount, setPaymentAmount] = useState<Number>(Number(0));
  const [pendingAmount, setPendingAmount] = useState<Number>(Number(0));
  const [creditnotesAmount, setCreditnotesAmount] = useState<Number>(Number(0));
  const [debitnotesAmount, setDebitnotesAmount] = useState<Number>(Number(0));
  const [paymentList, setPaymentList] = useState<Array<InvoicePaymentDataType>>([]);
  const suscriptionData = useAppSelector(state => state.suscription.suscription || {})

  useEffect(() => {
    if (invoiceId) {
      const run = async (invoiceId: string) => {
        try {
          const invoices = await InvoiceApi.get(invoiceId, {
            selectionSet: [
              ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
              "currency.*",
              "client.*",
              "creditnotes.*",
              "debitnotes.*",
              "invoicePayments.*",
              "invoicePayments.currency.*",
              "apliedCreditnote.*",
            ]
          });
          setInvoice(invoices);
          setTimeout(() => {
            modelRef.current?.refetch()
          });
        } finally {
          setIsLoading(false)
        }
      }
      run(invoiceId);
      setIsLoading(true)
    }
  }, [invoiceId]);

  useEffect(() => {
    calculatePaymentTotal(paymentList)
  }, [paymentList, invoice])

  const deleteRecord = async (item: InvoicePaymentDataType) => {
    const updateRecord: InvoicePaymentDataType = {
      estado: INVOICE_PAYMENT_CANCELLED_STATUS
    }
    try {
      await InvoicePaymentApi.update(`${item.id}`, updateRecord);
    } catch (err) {
      if (err instanceof Error) {
        showAlert('danger', err.message);
      } else {
        showAlert('success', 'El abono de la factura fue eliminado correctamente');
      }
    }

    modelRef.current?.refetch();
    calculatePaymentTotal(paymentList)
  }

  const calculatePaymentTotal = (paymentList: Array<InvoicePaymentDataType>) => {
    let invoiceTotal = Number(0);
    let creditnotesAmount = Number(0);
    let debitnotesAmount = Number(0);
    let paymentAmount = Number(0);
    if (paymentList && paymentList.length > 0) {
      paymentAmount = paymentList.filter(p => p.estado == INVOICE_PAYMENT_ACTIVE_STATUS).reduce((a, p) => {
        const amount = Number(p.monto)
        return a + amount;
      }, Number(0))
    }
    setPaymentAmount(paymentAmount)

    if (invoice && invoice.creditnotes && invoice.creditnotes?.length > 0) {
      creditnotesAmount = invoice.creditnotes?.filter(c => isValidEDocument(c.edocument_status_payload))
        .reduce((a: number, p: CreditnoteDataType) => {
          const amount = Number(p.total)
          return a + amount;
        }, Number(0))
    }
    setCreditnotesAmount(creditnotesAmount)

    if (invoice && invoice.debitnotes && invoice.debitnotes?.length > 0) {
      debitnotesAmount = invoice.debitnotes?.filter(d => isValidEDocument(d.edocument_status_payload))
        .reduce((a: number, p: DebitnoteDataType) => {
          const amount = Number(p.total)
          return a + amount;
        }, Number(0))
    }
    setDebitnotesAmount(debitnotesAmount)

    if (invoice.total) {
      invoiceTotal = invoice.total
    }
    setPendingAmount(invoiceTotal - paymentAmount - creditnotesAmount + debitnotesAmount)
  }

  const fetchPayments = async (amplifyClientFilter?: AmplifyFilter, token?: string, limit = 20): Promise<FetchResponse<InvoicePaymentDataType>> => {
    const payments = invoice.invoicePayments || []
    if (invoice?.apliedCreditnote) {
      const creditNote = invoice?.apliedCreditnote as CreditnoteDataType
      const payment: InvoicePaymentDataType = {
        created: creditNote.created,
        createdAt: creditNote.createdAt,
        updatedAt: creditNote.updatedAt,
        currency: invoice.currency,
        currencyId: invoice.currencyId,
        descripcion: `Asignacion de nota de credito #${creditNote.numero}`,
        estado: "active",
        monto: creditNote.total,
        formaPago: `${OTHER_PAYMENT_WAY_CODE}`
      }
      payments.push(payment)
    }

    return {
      items: payments.sort((a: InvoicePaymentDataType, b: InvoicePaymentDataType) => a.created == b.created ? -1 : 1)
    }
  }

  const onPaymentCreated = (generateInvoice: InvoicePaymentDataType) => {
    modelRef.current?.refetch()
  }

  const printInvoicePayment = (invoicePayment: InvoicePaymentDataType) => {
    if (suscriptionData?.formato_impresion === TICKET_PRINT_FORMANT_ID) {
      checkInvoicePaymentTicketPrint(invoicePayment)
    } else {
      window.open(`/user/invoices/payments/print/${invoicePayment.id}`, "_blank")
    }
  }

  return (
    <PageContainer
      title="Lista Abonos de Factura"
      backUrl="/user/invoices"
      primaryActions={invoice.tipo == INVOICE_HOLDING_TYPE_CODE ? [
        {
          onClick: () => {
            OpenInvoicePaymentPubSub.publish({
              openOffCanvas: true,
              invoiceId: `${invoiceId}`,
              pendingAmount: Number(pendingAmount),
              currencyId: `${invoice.currencyId}`,
              invoiceNumero: Number(invoice.numero)
            })
          },
          actionTitle: "Nuevo Pago",
          color: "success"
        }
      ] : []
      }
    >
      <Container fluid>
        <Row>
          <Col xs="2" md="2">
            <h5><b>Client:</b></h5>
          </Col>
          <Col>
            <h5><b>{invoice?.client?.nombre}</b></h5>
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Fecha:</b>
          </Col>
          <Col>
            {formatDate(invoice?.fecha)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Numero Factura:</b>
          </Col>
          <Col>
            {invoice?.sequence}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Subtotal:</b>
          </Col>
          <Col>
            {formatMoneyValue(invoice?.subtotal, invoice?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Descuento:</b>
          </Col>
          <Col>
            {formatMoneyValue(invoice?.descuento, invoice?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <b>Impuestos:</b>
          </Col>
          <Col>
            {formatMoneyValue(invoice?.impuestos, invoice?.currency?.currency_id)}
          </Col>
        </Row>
        <Row>
          <Col xs="2" md="2">
            <h5><b>Total:</b></h5>
          </Col>
          <Col>
            <h5><b>{formatMoneyValue(invoice?.total, invoice?.currency?.currency_id)}</b></h5>
          </Col>
        </Row>
        <hr />
      </Container>
      <ModelIndex<InvoicePaymentDataType>
        mandatoryFilters={[{ invoiceId: { eq: invoiceId } }]}
        messages={{
          noResults: "No hay abonos para este factura"
        }}
        ref={modelRef}
        onDataFetched={(data) => setPaymentList(data)}
        fetchData={(...args) => fetchPayments(...args)}
        primaryProperties={[
          {
            key: () => "Forma de Pago",
            optionalHighlighted: (val: any, data: InvoicePaymentDataType) => { return data.estado === INVOICE_PAYMENT_CANCELLED_STATUS },
            highlightedColors: {
              "true": {
                "bg": "#dc3545",
                "text": "#fff"
              },
              "false": {
                "bg": "#337ab7",
                "text": "#fff"
              }
            },
            highlighted: true
          },
          {
            key: (d) => { return getPaymentMethodLabel(d.formaPago) },
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "2", xs: "4" },
            { sm: "8", xs: "8" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              return formatDate(data.created)
            },
            title: "Fecha",
          },
          {
            key: "descripcion",
            title: "Descripcion",
          },
          {
            key: (data) => {
              return formatMoneyValue(data.monto, data.currency.currency_id)
            },
            title: "Total",
            highlighted: true
          },
          {
            key: (data) => `${data.user || "-"}`,
            title: "Usuario",
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "3", md: "2" },
            { sm: "4", md: "4" },
            { sm: "2", md: "3" },
            { sm: "3", md: "3" }
          ]
        }
        listActions={[
          {
            isVisible: (item: InvoicePaymentDataType) => item.estado != INVOICE_PAYMENT_CANCELLED_STATUS && !!item.id,
            actionTitle: "Eliminar Abono",
            iconProps: { icon: faTrash },
            needConfirm: true,
            confirmProps: {
              title: "Eliminar Abono",
              confirmMessage: "¿Seguro que desea eliminar esta Abono?",
              confirmBtnText: "Eliminar"
            },
            onClick: (id: string, item: InvoicePaymentDataType) => { deleteRecord(item) }
          },
          {
            href: "/user/invoices/payments/print/{:id}",
            target: '_blank',
            actionTitle: "Ver Abono",
            iconProps: { icon: faEye }
          },
          {
            onClick: (id: string, data: InvoicePaymentDataType) => printInvoicePayment(data),
            target: '_blank',
            actionTitle: "Imprimir Abono",
            iconProps: { icon: faPrint }
          },
        ]}
      >
      </ModelIndex>
      <hr />
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Total:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(invoice?.total, invoice?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Total Notas de Debito:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(debitnotesAmount, invoice?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Total Notas de Credito:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(creditnotesAmount, invoice?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Total Abonos:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(paymentAmount, invoice?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <Row>
        <Col xs="6" md="6" className="text-end">
          <h3><b>Saldo:</b></h3>
        </Col>
        <Col>
          <h3><b>{formatMoneyValue(pendingAmount, invoice?.currency?.currency_id)}</b></h3>
        </Col>
      </Row>
      <LoadingScreen isLoading={isLoading} />
      <InvoicePaymentOffCanvas
        onPaymentCreated={onPaymentCreated}
        suscriptionData={suscriptionData}
      />
    </PageContainer >
  );
}

export default InvoicePaymentIndex;
